import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { listAllOrgs, listMyOrgs } from "../api/dpp.js";
import { useAuth } from "../auth/AuthContext.js";
import type { OrgWithMemberRole } from "../types.js";

/**
 * Selected-organization context. The user's orgs load once per session (after
 * login); the selection persists in localStorage so a reload lands back in the
 * same tenant. Every DPP page reads `org` and scopes its API calls with it.
 *
 * A platform admin (global `org:manage`) sees EVERY org, not just the ones they
 * belong to: the API already lets them act cross-tenant (org-scope bypass), so
 * the switcher is how they step into a tenant to manage its products/passports.
 */

/**
 * Every org for a platform admin, carrying their real membership role where they
 * have one. Non-member orgs are marked "admin" because the API grants a platform
 * admin the full org-permission set there; the role only drives UI gating.
 */
async function loadPlatformOrgs(): Promise<OrgWithMemberRole[]> {
  const [all, mine] = await Promise.all([listAllOrgs(true), listMyOrgs()]);
  const roleById = new Map(mine.map((o) => [o.id, o.memberRole]));
  return all.map((o) => {
    const role = roleById.get(o.id);
    return role ? { ...o, memberRole: role } : { ...o, memberRole: "admin" as const, viaPlatform: true };
  });
}

const ORG_STORAGE_KEY = "dpp.selectedOrgId";

interface OrgState {
  orgs: OrgWithMemberRole[];
  /** The selected org, or null while loading / when the user has no orgs. */
  org: OrgWithMemberRole | null;
  loading: boolean;
  selectOrg: (orgId: string) => void;
  refreshOrgs: (selectId?: string) => Promise<void>;
}

const OrgContext = createContext<OrgState | null>(null);

function storedOrgId(): string | null {
  try {
    return localStorage.getItem(ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeOrgId(id: string): void {
  try {
    localStorage.setItem(ORG_STORAGE_KEY, id);
  } catch {
    /* storage unavailable — selection lives in memory only */
  }
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user, hasPermission } = useAuth();
  const platformAdmin = hasPermission("org:manage");
  const [orgs, setOrgs] = useState<OrgWithMemberRole[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(storedOrgId);
  const [loading, setLoading] = useState(true);

  const load = async (selectId?: string) => {
    try {
      const list = platformAdmin ? await loadPlatformOrgs() : await listMyOrgs();
      setOrgs(list);
      const wanted = selectId ?? storedOrgId();
      const next = list.find((o) => o.id === wanted) ?? list[0];
      if (next) {
        setSelectedId(next.id);
        storeOrgId(next.id);
      } else {
        setSelectedId(null);
      }
    } catch {
      setOrgs([]);
      setSelectedId(null);
    }
  };

  useEffect(() => {
    if (!user) {
      // Logged out: drop tenant state so the next user never sees stale orgs.
      setOrgs([]);
      setSelectedId(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void load().finally(() => setLoading(false));
    // Re-fetch when the authenticated user changes (login/logout).
  }, [user?.id, platformAdmin]);

  const value = useMemo<OrgState>(
    () => ({
      orgs,
      org: orgs.find((o) => o.id === selectedId) ?? null,
      loading,
      selectOrg: (orgId) => {
        setSelectedId(orgId);
        storeOrgId(orgId);
      },
      refreshOrgs: load,
    }),
    [orgs, selectedId, loading],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrgs(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrgs must be used within <OrgProvider>");
  return ctx;
}
